/**
 * The three layers of a board (SRS C8).
 *
 * Bottom to top: the paper, everything drawn in the lesson, and a layer only the
 * teacher can see. The paper is the canvas's own background and is not a place work
 * can be put, so what a stroke or an object actually carries is one of two names.
 *
 * The teacher-only layer is the one that matters. A teacher writes the answers on it
 * while the class works on the question, and nothing on it may ever reach a student's
 * screen — so a student's board never takes it in at all (board-sync), rather than
 * taking it in and remembering not to draw it.
 */

import type { Layer, Role } from '@opal/shared';

/** The two layers work can be put on, bottom first. */
export const WORK_LAYERS: readonly Layer[] = ['content', 'teacher'];

export const LAYER_LABELS: Readonly<Record<Layer, string>> = {
  content: 'Everyone',
  teacher: 'Only you',
};

/** Where a layer sits in the stack: higher paints later, and so on top. */
export function layerRank(layer: Layer): number {
  return layer === 'teacher' ? 1 : 0;
}

/** SRS D4 — a student never sees the teacher-only layer. */
export function canSeeLayer(role: Role, layer: Layer): boolean {
  return layer !== 'teacher' || role !== 'student';
}

/** True when this person may be shown this piece of work. */
export function visibleTo(role: Role, work: { readonly layer: Layer }): boolean {
  return canSeeLayer(role, work.layer);
}
